import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCatsOpen, setMobileCatsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileRef = useRef<HTMLDivElement>(null);
  const mobilePanelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setCategoriesOpen(false);
      }
      if (mobileRef.current && !mobileRef.current.contains(e.target as Node)) {
        if (!mobilePanelRef.current || !mobilePanelRef.current.contains(e.target as Node)) {
          setMobileOpen(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const closeAll = () => {
    setMobileOpen(false);
    setCategoriesOpen(false);
    setMobileCatsOpen(false);
  };

  const scrollToSection = (sectionId?: string) => {
    closeAll();
    if (location.pathname !== '/') {
      navigate('/');
      if (sectionId) {
        setTimeout(() => {
          const el = document.getElementById(sectionId);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 150);
      } else {
        setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 150);
      }
    } else {
      if (sectionId) {
        const el = document.getElementById(sectionId);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const navTextClass = (isActive = false) => {
    const base = scrolled
      ? 'text-foreground-700 hover:text-primary-500'
      : 'text-white/90 hover:text-white';
    return `text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${base} ${isActive ? 'font-semibold' : ''}`;
  };

  return (
    <>
      <nav
        ref={mobileRef}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
            : 'bg-black/30 backdrop-blur-sm'
        }`}
      >
        <div className="w-full px-4 md:px-8 lg:px-10">
          <div className="flex items-center justify-between h-14 md:h-16">
            {/* Logo */}
            <Link to="/" onClick={() => scrollToSection()} className="flex items-center gap-2 whitespace-nowrap cursor-pointer flex-shrink-0">
              <div className="w-7 h-7 md:w-8 md:h-8 rounded-lg bg-accent-500 flex items-center justify-center">
                <i className="ri-file-list-3-line text-white text-sm md:text-base"></i>
              </div>
              <span className={`font-heading text-sm md:text-lg font-semibold ${scrolled ? 'text-foreground-900' : 'text-white'}`}>
                cssbuyspreadsheet<span className="text-accent-400">.team</span>
              </span>
            </Link>

            {/* Desktop Nav Links */}
            <div className="hidden lg:flex items-center gap-6 xl:gap-8">
              <button onClick={() => scrollToSection()} className={navTextClass()}>
                Início
              </button>

              <button onClick={() => scrollToSection('guia')} className={navTextClass()}>
                Guia de Compras
              </button>

              {/* Categories Dropdown */}
              <div ref={dropdownRef} className="relative">
                <button
                  onClick={() => setCategoriesOpen(!categoriesOpen)}
                  className={`flex items-center gap-1 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${scrolled ? 'text-foreground-700 hover:text-primary-500' : 'text-white/90 hover:text-white'}`}
                >
                  Categorias
                  <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${categoriesOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {categoriesOpen && (
                  <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-72 bg-white rounded-xl p-3 shadow-2xl border border-background-200/70 z-50">
                    <div className="grid grid-cols-2 gap-1">
                      {categories.map((cat) => (
                        <a
                          key={cat.name}
                          href={cat.url}
                          target="_blank"
                          rel="noopener nofollow"
                          className="block px-3 py-2 text-sm text-foreground-700 hover:text-accent-600 hover:bg-accent-50 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                          onClick={() => setCategoriesOpen(false)}
                        >
                          {cat.name}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <button onClick={() => scrollToSection('artigos')} className={navTextClass()}>
                Artigos
              </button>

              <button onClick={() => scrollToSection('sobre')} className={navTextClass()}>
                Sobre
              </button>
            </div>

            {/* Desktop CTA + Mobile Toggle */}
            <div className="flex items-center gap-2 md:gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className={`hidden sm:inline-flex items-center gap-1.5 px-3 md:px-4 py-2 text-xs md:text-sm font-medium rounded-full border cursor-pointer whitespace-nowrap transition-all ${scrolled ? 'border-primary-400 text-primary-600 hover:bg-primary-50' : 'border-white/50 text-white hover:bg-white/10'}`}
              >
                CSSBuy Sheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="hidden sm:inline-flex items-center gap-1.5 px-3 md:px-4 py-2 text-xs md:text-sm font-semibold rounded-full bg-accent-500 text-background-50 hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Buy It Now
                <i className="ri-arrow-right-line text-sm"></i>
              </a>

              {/* Mobile Toggle */}
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className={`lg:hidden flex items-center justify-center w-9 h-9 rounded-lg cursor-pointer transition-colors ${scrolled ? 'text-foreground-700 hover:bg-background-200' : 'text-white hover:bg-white/10'}`}
                aria-label="Menu"
              >
                <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Panel — completely outside nav to avoid stacking issues */}
      <div
        ref={mobilePanelRef}
        className={`lg:hidden fixed top-14 left-0 right-0 bottom-0 z-[70] bg-white transition-transform duration-300 ease-out ${
          mobileOpen ? 'translate-x-0' : 'translate-x-full pointer-events-none'
        }`}
      >
        <div className="flex flex-col h-full overflow-y-auto px-6 py-6">
          <div className="flex flex-col gap-1 mb-8">
            <button
              onClick={() => scrollToSection()}
              className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer text-left"
            >
              <i className="ri-home-4-line text-lg text-primary-500 w-6 h-6 flex items-center justify-center"></i>
              <span className="text-sm font-medium">Início</span>
            </button>

            <button
              onClick={() => scrollToSection('guia')}
              className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer text-left"
            >
              <i className="ri-book-open-line text-lg text-accent-500 w-6 h-6 flex items-center justify-center"></i>
              <span className="text-sm font-medium">Guia de Compras</span>
            </button>

            <div>
              <button
                onClick={() => setMobileCatsOpen(!mobileCatsOpen)}
                className="flex items-center justify-between w-full px-4 py-3.5 rounded-xl text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <i className="ri-grid-line text-lg text-primary-500 w-6 h-6 flex items-center justify-center"></i>
                  <span className="text-sm font-medium">Categorias</span>
                </div>
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileCatsOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {mobileCatsOpen && (
                <div className="ml-11 mt-1 grid grid-cols-1 gap-0.5 bg-background-100 rounded-xl p-2">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-3 py-2.5 text-sm text-foreground-600 hover:text-accent-600 hover:bg-accent-50 rounded-lg transition-colors cursor-pointer"
                      onClick={closeAll}
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={() => scrollToSection('artigos')}
              className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer text-left"
            >
              <i className="ri-article-line text-lg text-accent-500 w-6 h-6 flex items-center justify-center"></i>
              <span className="text-sm font-medium">Artigos</span>
            </button>

            <button
              onClick={() => scrollToSection('sobre')}
              className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer text-left"
            >
              <i className="ri-information-line text-lg text-foreground-500 w-6 h-6 flex items-center justify-center"></i>
              <span className="text-sm font-medium">Sobre</span>
            </button>
          </div>

          <div className="border-t border-background-200 pt-6 flex flex-col gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={closeAll}
              className="flex items-center justify-center gap-2 px-5 py-3 rounded-full border-2 border-primary-500 text-primary-600 font-semibold text-sm hover:bg-primary-50 transition-colors cursor-pointer"
            >
              CSSBuy Sheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={closeAll}
              className="flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-accent-500 text-background-50 font-semibold text-sm hover:bg-accent-600 transition-colors cursor-pointer"
            >
              Buy It Now
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      </div>
    </>
  );
}